import { useCallback, useEffect, useMemo, useState } from "react"
import type {
  TemplateConfiguration,
  TemplateField,
  TemplateSection,
} from "shared/api/templateService"
import type { UploadedFileItem } from "modules/permit/components/RequiredDocumentsSection"
import { GENERAL_FIELD_STATE_MAP } from "modules/permit/utils/permitFieldMapping"
import { buildSubactivityFieldKey } from "modules/permit/components/SubActivitiesSection"
import { buildRiskAssessmentPayload, type RiskState } from "modules/permit/utils/permitHelpers"
import type { PermitFormState } from "../types"
import type { RequiredDocumentItem } from "./useRequiredDocuments"
import {
  buildRequiredDocumentValidationKey,
  buildRiskValidationKey,
  buildSubactivityValidationKey,
  buildTemplateFieldValidationKey,
} from "../utils/validationTargets"

interface UsePermitValidationParams {
  templateConfig: TemplateConfiguration | null
  generalSection: TemplateSection | null
  stageOwnedSections: TemplateSection[]
  permitData: PermitFormState
  extraFieldValues: Record<string, string>
  selectedActivities: string[]
  riskData: RiskState
  requiredDocuments: RequiredDocumentItem[]
  uploadedFiles: UploadedFileItem[]
  canApproveCurrentStage: boolean
}

interface UsePermitValidationResult {
  missingApprovalStageFields: string[]
  missingRequiredFields: string[]
  missingMandatoryDocuments: RequiredDocumentItem[]
  stageValidationError: string | null
  requiredFieldSubmitError: string | null
  submitValidationTargetKeys: string[]
  submitValidationAttemptToken: number
  ensureStageRequirementsSatisfied: () => boolean
  validateBeforeSubmit: () => boolean
}

interface ValidationTarget {
  key: string
  label: string
}

const hasFieldValue = (value?: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === "string") return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === "boolean") return value
  return true
}

const formatListPreview = (items: string[], limit = 3): string => {
  if (items.length <= limit) {
    return items.join(", ")
  }
  const summarized = items.slice(0, limit).join(", ")
  return `${summarized} และอีก ${items.length - limit} รายการ`
}

const formatDocumentPreview = (docs: RequiredDocumentItem[], limit = 3): string => {
  if (docs.length <= limit) {
    return docs.map((doc) => doc.name).join(", ")
  }
  const summarized = docs
    .slice(0, limit)
    .map((doc) => doc.name)
    .join(", ")
  return `${summarized} และอีก ${docs.length - limit} รายการ`
}

const dedupeValidationTargets = (targets: ValidationTarget[]) => {
  const unique = new Map<string, ValidationTarget>()
  targets.forEach((target) => {
    if (!unique.has(target.key)) {
      unique.set(target.key, target)
    }
  })
  return Array.from(unique.values())
}

export const usePermitValidation = ({
  templateConfig,
  generalSection,
  stageOwnedSections,
  permitData,
  extraFieldValues,
  selectedActivities,
  riskData,
  requiredDocuments,
  uploadedFiles,
  canApproveCurrentStage,
}: UsePermitValidationParams): UsePermitValidationResult => {
  const isGeneralEnabled = templateConfig?.generalEnabled !== false
  const isSubactivityEnabled = templateConfig?.subactivityEnabled !== false
  const isRamEnabled = templateConfig?.ramEnabled !== false
  const isAttachmentEnabled = templateConfig?.attachmentEnabled !== false
  const hasRiskAssessmentScore = useMemo(
    () => !isRamEnabled || buildRiskAssessmentPayload(riskData) !== null,
    [isRamEnabled, riskData],
  )

  const parseFieldConfig = useCallback((field: TemplateField): any | null => {
    if (!field.config) return null
    const trimmed = field.config.trim()
    if (!trimmed) return null
    try {
      return JSON.parse(trimmed) as any
    } catch {
      return null
    }
  }, [])

  const readValueByCode = useCallback(
    (code?: string): string | undefined => {
      if (!code) return undefined
      const upper = code.toUpperCase()
      const stateKey = GENERAL_FIELD_STATE_MAP[upper] as keyof PermitFormState | undefined
      if (stateKey) {
        const stateValue = permitData[stateKey]
        if (typeof stateValue === "string" && stateValue.trim().length > 0) {
          return stateValue.trim()
        }
        if (stateValue && typeof stateValue === "object" && "value" in (stateValue as any)) {
          const value = (stateValue as any).value
          if (typeof value === "string" && value.trim().length > 0) {
            return value.trim()
          }
        }
      }

      const candidates = [code, upper, code.toLowerCase()]
      for (const key of candidates) {
        const value = extraFieldValues[key]
        if (typeof value === "string" && value.trim().length > 0) {
          return value.trim()
        }
      }
      return undefined
    },
    [extraFieldValues, permitData],
  )

  const readExtraFieldValue = useCallback(
    (field: TemplateField): string | undefined => {
      const candidates = new Set<string>()
      if (field.fieldId) {
        candidates.add(field.fieldId)
      }
      if (field.code) {
        candidates.add(field.code)
        candidates.add(field.code.toUpperCase())
        candidates.add(field.code.toLowerCase())
      }
      const possibleKeys = Array.from(candidates)
      for (let idx = 0; idx < possibleKeys.length; idx += 1) {
        const key = possibleKeys[idx]
        if (!key) {
          continue
        }
        const value = extraFieldValues[key]
        if (typeof value === "string" && value.trim().length > 0) {
          return value
        }
      }
      return undefined
    },
    [extraFieldValues],
  )

  const readFieldValue = useCallback(
    (field?: TemplateField): string | undefined => {
      if (!field) {
        return undefined
      }
      const code = field.code?.toUpperCase()
      if (code && GENERAL_FIELD_STATE_MAP[code]) {
        if (code === "CONTROLLER") {
          return permitData.controller?.value ?? permitData.controller?.label ?? undefined
        }
        const stateKey = GENERAL_FIELD_STATE_MAP[code] as keyof PermitFormState
        const stateValue = permitData[stateKey]
        if (typeof stateValue === "string") {
          const normalized = stateValue.trim()
          if (normalized.length > 0) {
            return normalized
          }
        }
        if (stateValue != null) {
          const normalized = String(stateValue).trim()
          if (normalized.length > 0) {
            return normalized
          }
        }
        const fallback = readExtraFieldValue(field)
        if (fallback) {
          return fallback
        }
        return undefined
      }
      return readExtraFieldValue(field)
    },
    [permitData, readExtraFieldValue],
  )

  const buildMissingFieldTarget = useCallback(
    (field?: TemplateField, sectionLabel?: string): ValidationTarget | null => {
      if (!field || !field.required) {
        return null
      }

      if (field.visibilityRules) {
        try {
          const rules = JSON.parse(field.visibilityRules)
          const ruleList = Array.isArray(rules) ? rules : [rules]
          const shouldRequire = ruleList.every((rule) => {
            if (!rule || typeof rule !== "object") return true
            const depCode =
              (rule.fieldCode as string) ??
              (rule.field_code as string) ??
              (rule.code as string)
            if (!depCode) return true
            const depValue = readValueByCode(depCode)
            if (!hasFieldValue(depValue)) return false
            if (rule.equals !== undefined && rule.equals !== null) {
              return String(depValue).trim().toLowerCase() === String(rule.equals).trim().toLowerCase()
            }
            return true
          })
          if (!shouldRequire) {
            return null
          }
        } catch {
          // ignore parse errors; fall back to normal validation
        }
      }

      const config = parseFieldConfig(field)
      if (config) {
        const rules: Array<{ code: string; required?: boolean }> = []
        if (typeof config.dependsOn === "string" && config.dependsOn.trim().length > 0) {
          rules.push({ code: config.dependsOn, required: config.dependencyConfig?.requiredForLoad })
        }
        if (Array.isArray(config.extraDependencies)) {
          config.extraDependencies.forEach((rule: any) => {
            if (rule && typeof rule.fieldCode === "string" && rule.fieldCode.trim().length > 0) {
              rules.push({ code: rule.fieldCode, required: rule.requiredForLoad })
            }
          })
        }
        const dependencyMissing = rules.some((rule) => {
          if (rule.required === false) {
            return false
          }
          const value = readValueByCode(rule.code)
          return !hasFieldValue(value)
        })
        if (dependencyMissing) {
          return null
        }
      }

      const fieldType = (field.fieldType ?? "").toLowerCase()
      if (fieldType === "computed") {
        return null
      }

      const currentValue = readFieldValue(field)
      if (hasFieldValue(currentValue)) {
        return null
      }

      const fieldLabel = field.label?.trim() || field.code || field.fieldId || "ฟิลด์ที่ต้องกรอก"
      const key = buildTemplateFieldValidationKey(field)
      if (!key) {
        return null
      }

      return {
        key,
        label: sectionLabel ? `${sectionLabel}: ${fieldLabel}` : fieldLabel,
      }
    },
    [parseFieldConfig, readFieldValue, readValueByCode],
  )

  const missingApprovalStageFields = useMemo(() => {
    if (stageOwnedSections.length === 0) {
      return []
    }
    const missing = new Set<string>()
    stageOwnedSections.forEach((section) => {
      const sectionLabel = section.title ?? section.code ?? undefined
      section.fields?.forEach((field) => {
        const target = buildMissingFieldTarget(field, sectionLabel)
        if (target?.label) {
          missing.add(target.label)
        }
      })
    })
    return Array.from(missing)
  }, [stageOwnedSections, buildMissingFieldTarget])

  const missingRequiredFieldTargets = useMemo(() => {
    if (!templateConfig) {
      return []
    }

    const missing: ValidationTarget[] = []

    const registerMissingField = (field?: TemplateField, sectionLabel?: string) => {
      const target = buildMissingFieldTarget(field, sectionLabel)
      if (target) {
        missing.push(target)
      }
    }

    if (isGeneralEnabled) {
      generalSection?.fields?.forEach((field) =>
        registerMissingField(field, generalSection.title ?? generalSection.code ?? undefined),
      )
    }

    templateConfig.sections
      ?.filter((section) => {
        const code = section.code?.toUpperCase() ?? ""
        const isGeneralSection = code.startsWith("GENERAL")
        const isRequestorSection = !section.approvalStageId
        return isRequestorSection && !isGeneralSection
      })
      .forEach((section) => {
        section.fields?.forEach((field) =>
          registerMissingField(field, section.title ?? section.code ?? undefined),
        )
      })

    if (isSubactivityEnabled) {
      templateConfig.subActivities?.forEach((subactivity) => {
        if (!selectedActivities.includes(subactivity.subactivityId)) {
          return
        }
        subactivity.fields?.forEach((field) => {
          if (!field.required) {
            return
          }
          const key = buildSubactivityFieldKey(subactivity.subactivityId, field.subactivityFieldId)
          const value = extraFieldValues[key]
          if (typeof value === "string" && value.trim().length > 0) {
            return
          }
          const label =
            field.label?.trim() ||
            field.code ||
            `${subactivity.name ?? "Sub-Activity"} (${field.subactivityFieldId})`
          missing.push({
            key: buildSubactivityValidationKey(subactivity.subactivityId, field.subactivityFieldId),
            label,
          })
        })
      })
    }

    return dedupeValidationTargets(missing)
  }, [
    templateConfig,
    generalSection,
    selectedActivities,
    extraFieldValues,
    buildMissingFieldTarget,
    isGeneralEnabled,
    isSubactivityEnabled,
  ])

  const missingRequiredFields = useMemo(
    () => missingRequiredFieldTargets.map((target) => target.label),
    [missingRequiredFieldTargets],
  )

  const missingMandatoryDocuments = useMemo(() => {
    if (!isAttachmentEnabled) {
      return []
    }
    return requiredDocuments
      .filter((doc) => doc.isMandatory)
      .filter((doc) => {
        const normalizedDocId = doc.id?.trim().toLowerCase()
        if (!normalizedDocId) {
          return false
        }
        return !uploadedFiles.some((file) => file.docId?.trim().toLowerCase() === normalizedDocId)
      })
  }, [requiredDocuments, uploadedFiles, isAttachmentEnabled])

  const missingMandatoryDocumentTargets = useMemo(
    () =>
      missingMandatoryDocuments.map((doc) => ({
        key: buildRequiredDocumentValidationKey(doc.id),
        label: doc.name,
      })),
    [missingMandatoryDocuments],
  )

  const missingRiskAssessmentTargets = useMemo(() => {
    if (!isRamEnabled) {
      return []
    }

    const targets: ValidationTarget[] = []
    const consequenceRows = [
      { key: "personnel", label: "ด้านบุคคล" },
      { key: "equipment", label: "ด้านชุมชน" },
      { key: "environment", label: "ด้านสิ่งแวดล้อม" },
      { key: "property", label: "ด้านทรัพย์สิน" },
    ] as const
    const likelihoodRows = [
      { key: "conditions", label: "เชิงปริมาณ" },
      { key: "accident", label: "เชิงคุณภาพ" },
    ] as const

    consequenceRows.forEach((row) => {
      if (!hasFieldValue(riskData.consequence[row.key])) {
        targets.push({ key: buildRiskValidationKey(row.key), label: row.label })
      }
    })
    likelihoodRows.forEach((row) => {
      if (!hasFieldValue(riskData.likelihood[row.key])) {
        targets.push({ key: buildRiskValidationKey(row.key), label: row.label })
      }
    })

    return targets
  }, [isRamEnabled, riskData.consequence, riskData.likelihood])

  const allSubmitValidationTargets = useMemo(
    () =>
      dedupeValidationTargets([
        ...missingRequiredFieldTargets,
        ...missingMandatoryDocumentTargets,
        ...missingRiskAssessmentTargets,
      ]),
    [missingMandatoryDocumentTargets, missingRequiredFieldTargets, missingRiskAssessmentTargets],
  )

  const [requiredFieldSubmitError, setRequiredFieldSubmitError] = useState<string | null>(null)
  const [stageValidationError, setStageValidationError] = useState<string | null>(null)
  const [submitValidationTargetKeys, setSubmitValidationTargetKeys] = useState<string[]>([])
  const [submitValidationAttemptToken, setSubmitValidationAttemptToken] = useState(0)

  useEffect(() => {
    if ((!canApproveCurrentStage || missingApprovalStageFields.length === 0) && stageValidationError) {
      setStageValidationError(null)
    }
  }, [canApproveCurrentStage, missingApprovalStageFields.length, stageValidationError])

  useEffect(() => {
    if (
      missingRequiredFields.length === 0 &&
      missingMandatoryDocuments.length === 0 &&
      hasRiskAssessmentScore &&
      requiredFieldSubmitError
    ) {
      setRequiredFieldSubmitError(null)
    }
  }, [
    hasRiskAssessmentScore,
    missingRequiredFields.length,
    missingMandatoryDocuments.length,
    requiredFieldSubmitError,
  ])

  useEffect(() => {
    if (submitValidationTargetKeys.length === 0) {
      return
    }

    const activeKeys = new Set(
      [
        ...missingRequiredFieldTargets,
        ...missingMandatoryDocumentTargets,
        ...missingRiskAssessmentTargets,
      ].map((target) => target.key),
    )

    const nextKeys = submitValidationTargetKeys.filter((key) => activeKeys.has(key))
    const didChange =
      nextKeys.length !== submitValidationTargetKeys.length ||
      nextKeys.some((key, index) => submitValidationTargetKeys[index] !== key)

    if (didChange) {
      setSubmitValidationTargetKeys(nextKeys)
    }
  }, [
    missingMandatoryDocumentTargets,
    missingRequiredFieldTargets,
    missingRiskAssessmentTargets,
    submitValidationTargetKeys,
  ])

  const ensureStageRequirementsSatisfied = useCallback(() => {
    if (!canApproveCurrentStage) {
      return false
    }
    if (missingApprovalStageFields.length === 0) {
      setStageValidationError(null)
      return true
    }

    try {
      const stageFieldDetails =
        stageOwnedSections?.flatMap((section) =>
          (section.fields ?? []).map((field) => {
            const value = readFieldValue(field)
            const missing = !hasFieldValue(value)
            return {
              section: section.title ?? section.code,
              code: field.code,
              fieldId: field.fieldId,
              label: field.label,
              required: field.required,
              value,
              missing,
            }
          }),
        ) ?? []

      const missingDetails = stageFieldDetails.filter(
        (entry) =>
          entry.missing &&
          (missingApprovalStageFields.length === 0 ||
            missingApprovalStageFields.some((label) => (entry.label ?? "").includes(label))),
      )

      console.log("[permit][validation] approval stage check", {
        missing: missingApprovalStageFields,
        missingDetails,
        allStageFields: stageFieldDetails,
      })
    } catch {
      // best-effort debug log; ignore failures
    }

    const preview = formatListPreview(missingApprovalStageFields)
    setStageValidationError(`กรุณากรอกข้อมูลให้ครบในขั้นตอนนี้ (${preview})`)
    return false
  }, [canApproveCurrentStage, missingApprovalStageFields, readFieldValue, stageOwnedSections])

  const validateBeforeSubmit = useCallback(() => {
    const highlightAllCurrentTargets = () => {
      setSubmitValidationTargetKeys(allSubmitValidationTargets.map((target) => target.key))
      setSubmitValidationAttemptToken((token) => token + 1)
    }

    if (missingRequiredFields.length > 0) {
      const preview = formatListPreview(missingRequiredFields)
      setRequiredFieldSubmitError(`กรุณากรอกข้อมูลบังคับให้ครบ (${preview})`)
      highlightAllCurrentTargets()
      return false
    }
    if (missingMandatoryDocuments.length > 0) {
      const preview = formatDocumentPreview(missingMandatoryDocuments)
      setRequiredFieldSubmitError(`กรุณาอัปโหลดเอกสารที่จำเป็น: ${preview}`)
      highlightAllCurrentTargets()
      return false
    }
    if (!hasRiskAssessmentScore) {
      setRequiredFieldSubmitError("กรุณาประเมิน RAM ให้ครบเพื่อให้ระบบคำนวณ Risk Assessment Score ก่อนส่งอนุมัติ")
      highlightAllCurrentTargets()
      return false
    }
    setRequiredFieldSubmitError(null)
    setSubmitValidationTargetKeys([])
    return true
  }, [
    allSubmitValidationTargets,
    hasRiskAssessmentScore,
    missingMandatoryDocuments,
    missingRequiredFields,
  ])

  return {
    missingApprovalStageFields,
    missingRequiredFields,
    missingMandatoryDocuments,
    stageValidationError,
    requiredFieldSubmitError,
    submitValidationTargetKeys,
    submitValidationAttemptToken,
    ensureStageRequirementsSatisfied,
    validateBeforeSubmit,
  }
}
